import React, { useCallback } from 'react';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useGetExpiredPlanTrialData } from './use-get-expired-plan-trial-data';
import { HAS_DISMISSED_PLAN_TRIAL_EXPIRED_BANNER, useUserSpecificLocalStorage } from '../../../hooks/use-local-storage';
import { messages } from '../messages';
import { PageEvents, PageEventSourceStrings, useTrackEvent } from '../../../pages/settings/payment-and-subscription/hooks/use-track-event';

import style from '../header-notification-banner.module.less';

type BillingPlanTrialExpiredBannerProps = {
  onUpgradeClick: (billingPlanId: number) => void;
};

export const BillingPlanTrialExpiredBanner = ({ onUpgradeClick }: BillingPlanTrialExpiredBannerProps) => {
  const { expiredPlanTierName, expiredPlanCode, expiredBillingPlanId } = useGetExpiredPlanTrialData();
  // Key includes planCode so each tier has its own dismissal state
  const planSpecificKey = `${HAS_DISMISSED_PLAN_TRIAL_EXPIRED_BANNER}/${expiredPlanCode}`;
  const [, setDismissed] = useUserSpecificLocalStorage(planSpecificKey);
  const trackDismiss = useTrackEvent(PageEvents.PLAN_TRIAL_EXPIRED_BANNER_DISMISS, PageEventSourceStrings.PLAN_TRIAL_EXPIRED_BANNER);
  const trackUpgradeClick = useTrackEvent(
    PageEvents.PLAN_TRIAL_EXPIRED_BANNER_UPGRADE_CLICK,
    PageEventSourceStrings.PLAN_TRIAL_EXPIRED_BANNER,
  );

  const handleClose = useCallback(() => {
    trackDismiss({ planTierName: expiredPlanTierName });
    setDismissed(true);
  }, [setDismissed, trackDismiss, expiredPlanTierName]);

  const handleUpgradeClick = useCallback(() => {
    trackUpgradeClick({ planTierName: expiredPlanTierName });
    onUpgradeClick(expiredBillingPlanId);
  }, [onUpgradeClick, trackUpgradeClick, expiredPlanTierName, expiredBillingPlanId]);

  return (
    <BannerMessageV2
      buttons={
        <Button onClick={handleUpgradeClick} theme={THEME_CONSTANTS.white}>
          <Text textType={TextType.Caption} value={messages.upgradeYourPlan} />
        </Button>
      }
      className={style['banner-padding']}
      content={
        <Text
          formattedMessageValues={{
            planTierName: expiredPlanTierName,
          }}
          textType={TextType.Paragraph}
          value={messages.trialExpiredContent}
        />
      }
      layout={BannerLayout.Default}
      onClose={handleClose}
      status={BannerStatus.Warning}
    />
  );
};
